import { Link } from "@remix-run/react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PillTabs } from "#app/components/new-ui/pill-tabs";
import { ThemeSwitch } from "#app/routes/resources+/theme-switch.tsx";
import { ProviderConnectionForm } from "#app/utils/connections.tsx";
import { useRequestInfo } from "#app/utils/request-info.ts";
import { useOptionalUser } from "#app/utils/user";

const primaryButtonClassName =
  "flex h-9 sm:h-10 shrink-0 items-center border-none! bg-clip-border! justify-center rounded-full px-4 sm:px-6 py-2 text-sm capitalize text-white transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-white/30 bg-[linear-gradient(93.53deg,#141A42_14.58%,#6577A4_110.19%)]";

export function LandingHeader({
  landingPrompt = "",
  children,
  tabs,
  tabValue,
  onTabValueChange,
  backTo,
  backLabel,
}: {
  showLogin?: boolean;
  landingPrompt?: string;
  children?: React.ReactNode;
  tabs?: { value: "chat" | "marketplace"; label: string }[];
  tabValue?: "chat" | "marketplace";
  onTabValueChange?: (value: "chat" | "marketplace") => void;
  /** When set, shows a back arrow to the left of the logo (e.g. docs → app). */
  backTo?: string;
  backLabel?: string;
}) {
  const { t } = useTranslation(["common", "marketing"]);
  const user = useOptionalUser();
  const requestInfo = useRequestInfo();
  const resolvedBackLabel = backLabel ?? t("common:back");

  // Post-login destination for the header's own login form (below), NOT a nav
  // link — `auth.$provider.ts` writes it into the `redirectTo` cookie, where it
  // outranks the post-login default. This header renders on `/`, `/marketplace`
  // and `/login`, so it is the most common sign-in path on the site; leaving it
  // on `/marketplace` meant most people never reached the default at all.
  const tryForFreeRedirectTo =
    landingPrompt.trim() !== ""
      ? `/u/alpha/agent?prompt=${encodeURIComponent(landingPrompt.trim())}`
      : "/u/alpha/agent";

  let rightSlot: React.ReactNode;
  if (children) {
    rightSlot = children;
  } else if (user) {
    rightSlot = (
      <Link to="/marketplace" className={primaryButtonClassName}>
        {t("marketing:marketplace")}
      </Link>
    );
  } else {
    rightSlot = (
      <ProviderConnectionForm
        type="Login"
        providerName="oauth2"
        redirectTo={tryForFreeRedirectTo}
        buttonText={t("common:login")}
        buttonClassName={primaryButtonClassName}
      />
    );
  }

  return (
    <header className="left-0 right-0 top-0 z-50 w-full bg-white backdrop-blur-3xl dark:bg-zinc-950/70">
      <nav className="max-w-7xl mx-auto flex w-full items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex min-w-0 items-center gap-2">
          {backTo ? (
            <Link
              to={backTo}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[#535EA4] transition-colors hover:bg-[#F0F4FF] hover:text-[#141A42] dark:text-indigo-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
              aria-label={resolvedBackLabel}
              title={resolvedBackLabel}
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
          ) : null}
          <Link
            to="/"
            className="flex items-center gap-2.5 transition-opacity hover:opacity-90"
          >
            <img
              src="/img/supervity-logo.svg"
              alt=""
              className="size-6 shrink-0 dark:hidden"
            />
            <img
              src="/img/supervity-logo-dark.png"
              alt=""
              className="hidden size-6 shrink-0 dark:block"
            />
            <span className="text-lg sm:text-xl font-semibold leading-none tracking-tight text-[#141A42] dark:text-white max-[380px]:sr-only">
              Supervity
            </span>
          </Link>
        </div>
        <div className="absolute top-[90px] translate-x-[-50%] left-[50%] sm:relative sm:left-auto sm:top-auto sm:translate-x-0">
          {tabs && tabValue && onTabValueChange ? (
            <PillTabs
              tabs={tabs}
              value={tabValue}
              onValueChange={onTabValueChange}
              layoutId="marketing-landing-tabs"
              className="bg-white border border-zinc-200 sm:border-0 sm:bg-zinc-200/60 dark:border-zinc-700 dark:bg-zinc-900 sm:dark:bg-zinc-800/70"
            />
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {rightSlot}
          <ThemeSwitch userPreference={requestInfo.userPrefs.theme} />
        </div>
      </nav>
    </header>
  );
}
